import {
  Box,
  ButtonUnstyled,
  Divider,
  Link,
  Stack,
  styled,
  SwipeableDrawer,
  TextField,
  textFieldClasses,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import {useBoundingclientrectRef} from 'rooks';
import backgroundLoginSm from '../assets/background-login-sm.png';
import backgroundLoginLg from '../assets/background-login-lg.png';
import googleLogo from '../assets/google.png';
import linkedinLogo from '../assets/linkedin.png';
import {useAuth} from '../providers/auth';
import {grey} from '@mui/material/colors';

const drawerBleeding = 160;

const AuthContainer = styled('div')(({theme}) => ({
  backgroundImage: `url(${backgroundLoginSm})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  minWidth: '100vw',
  minHeight: '100vh',
  [theme.breakpoints.up('md')]: {
    backgroundImage: `url(${backgroundLoginLg})`,
  },
}));

const Puller = styled(Box)(({theme}) => ({
  width: 40,
  height: 6,
  backgroundColor: grey[400],
  borderRadius: 3,
  position: 'absolute',
  top: 12,
  left: 'calc(50% - 15px)',
}));

const Heading = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.pxToRem(40),
  fontFamily: 'Lato',
  fontWeight: '600',
  textAlign: 'center',
  color: theme.palette.common.black,
  maxWidth: theme.typography.pxToRem(250),
  lineHeight: '1.1em',
  margin: '0 auto',
  paddingTop: theme.typography.pxToRem(80),
  paddingBottom: theme.typography.pxToRem(80),
  [theme.breakpoints.up('md')]: {
    maxWidth: 'fit-content',
  },
}));

const LoginPaper = styled('div')(({theme}) => ({
  background: 'rgba(255, 255, 255, 0.3)',
  border: '1px solid #FFFFFF4D',
  borderRadius: '20px',
  borderBottomLeftRadius: '0',
  borderBottomRightRadius: '0',
  opacity: 1,
  backdropFilter: 'blur(32px)',
  padding: theme.spacing(2),
}));

const HelperText = styled(Typography)(({theme}) => ({
  fontSize: theme.typography.pxToRem(15),
  color: '#4D5562',
}));

const HelperLink = styled(Link)(({theme}) => ({
  fontSize: theme.typography.pxToRem(15),
  color: '#4D5562',
  textDecorationColor: '#4D5562',
}));

const TextInput = styled(TextField)({
  '& .MuiInputBase-root': {
    background: 'rgba(0,0,0,0.05)',
    borderRadius: '12px',
  },
  '& label': {
    color: '#00000080',
  },
  '& label.Mui-focused': {
    color: '#00000080',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderRadius: '12px',
      borderColor: 'rgba(0, 0, 0, 0.1)',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.15)',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'rgba(0, 0, 0, 0.15)',
    },
  },
});

const Button = styled(ButtonUnstyled)(({theme}) => ({
  background: '#5C87FF',
  border: '1px solid #0000001A',
  borderRadius: '12px',
  width: '100%',
  paddingBlock: 10,
  paddingInline: theme.spacing(2),
  color: '#FFFFFF',
  fontWeight: '500',
  fontSize: theme.typography.pxToRem(16),
  cursor: 'pointer',
  '&:hover': {
    background: '#5C87FFee',
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
  },
}));

const SocialButton = styled(Button)(({theme}) => ({
  background: '#FFFFFFB3',
  border: '1px solid #FFFFFF80',
  borderRadius: '12px',
  color: '#000000',
  '&:hover': {
    background: '#FFFFFFB3',
  },
  '&:disabled': {
    background: theme.palette.action.disabledBackground,
  },
}));

function LoginContentBottom() {
  const {login, isLoading} = useAuth();
  return (
    <>
      <Stack spacing={3}>
        <SocialButton disabled={isLoading} onClick={() => login('google')}>
          <Box sx={{mr: 1, mb: '-4px'}} component="img" src={googleLogo} alt="Google" />
          Sign in with Google
        </SocialButton>
        <SocialButton disabled={isLoading} onClick={() => login('linkedin')}>
          <Box sx={{mr: 1, mb: '-3px'}} component="img" src={linkedinLogo} alt="Linkedin" />
          Sign in with Linkedin
        </SocialButton>
      </Stack>
      <Stack spacing={1} sx={{mt: 3}}>
        <HelperText>
          Having problems signing up? <HelperLink>Contact us</HelperLink>
        </HelperText>
        <HelperLink>Terms of service</HelperLink>
      </Stack>
    </>
  );
}

function LoginContentTop() {
  const {login, isLoading} = useAuth();
  const [email, setEmail] = React.useState('');
  return (
    <>
      <Stack sx={{mt: 3}} spacing={3}>
        <TextInput
          value={email}
          onChange={e => setEmail(e.target.value)}
          type="email"
          label="Email"
          variant="outlined"
          size="small"
          fullWidth
          disabled={isLoading}
        />
        <Button
          disabled={isLoading}
          onClick={() => {
            login('passwordless', email);
          }}
        >
          Sign in
        </Button>
      </Stack>
    </>
  );
}

function LoginDrawer({children}: {children: React.ReactNode}) {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();
  const isBigScreen = useMediaQuery(theme.breakpoints.up('md'));
  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open || isBigScreen}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      swipeAreaWidth={drawerBleeding}
      disableSwipeToOpen={false}
      hideBackdrop
      elevation={0}
      ModalProps={{
        keepMounted: true,
      }}
      PaperProps={{
        sx: {
          background: 'none',
          px: 2,
          overflow: 'visible',
          maxWidth: '488px',
          mx: 'auto',
        },
      }}
    >
      {children}
    </SwipeableDrawer>
  );
}

function Auth() {
  const theme = useTheme();
  const isBigScreen = useMediaQuery(theme.breakpoints.up('md'));
  const [myRef, boundingClientRect] = useBoundingclientrectRef();

  const isSmallHeight = useMediaQuery('(max-height: 650px)');
  return (
    <AuthContainer>
      <Heading>Welcome to GVMO Live</Heading>
      {isSmallHeight && (
        <LoginPaper sx={{maxWidth: '488px', mx: 'auto'}}>
          <LoginContentTop />
          <Box sx={{my: 4}}>
            <Divider>or</Divider>
          </Box>
          <LoginContentBottom />
        </LoginPaper>
      )}
      {!isSmallHeight && (
        <LoginDrawer>
          <LoginPaper sx={{borderRadius: 0, borderTop: 0, position: 'relative'}}>
            <LoginPaper
              ref={myRef}
              sx={{
                position: 'absolute',
                top: `-${boundingClientRect?.height ?? 0}px`,
                visibility: 'visible',
                right: `-1px`,
                left: `-1px`,
                borderBottom: 0,
                backdropFilter: 'none',
              }}
            >
              {!isBigScreen && <Puller />}
              <LoginContentTop />
            </LoginPaper>
            <Box sx={{mb: 4}}>
              <Divider>or</Divider>
            </Box>
            <LoginContentBottom />
          </LoginPaper>
        </LoginDrawer>
      )}
    </AuthContainer>
  );
}

export default Auth;
